// semeia-disable-reexport
import dayjs from "dayjs";

/**
 * Return a string which displays the year if the duration is more than 3 years
 * or the months otherwise.
 *
 * @example
 * "14 mois"
 * "4 ans et 2 mois"
 *
 * @param firstDate
 * @param secondDate
 */
function durationBetweenDates(firstDate: Date, secondDate: Date): string {
  if (!firstDate || !secondDate) {
    return "";
  }

  const year = dayjs(secondDate).diff(firstDate, "year");
  const month = dayjs(secondDate).diff(firstDate, "month");

  if (year >= 3) {
    return `${year} an${year > 1 ? "s" : ""} et ${month - year * 12} mois`;
  }
  return `${month} mois`;
}

/**
 * Return the difference between the two date in hours
 * @param firstDate
 * @param secondDate
 * @return number of hours.
 */
function hoursDiffBetweenDates(firstDate: Date, secondDate: Date): number {
  const diffMs = Math.abs(firstDate.getTime() - secondDate.getTime()); // milliseconds between now & Christmas
  return Math.floor((diffMs % 86400000) / 3600000); // hours
}

/**
 * Return the difference between the two date in days no matter the years or hours.
 * @param firstDate
 * @param secondDate
 * @return number of days.
 */
function dayDiffBetweenDates(firstDate: Date, secondDate: Date): number {
  const firstMoment = dayjs(firstDate)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);
  const secondMoment = dayjs(secondDate)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);
  return Math.ceil(Math.abs(firstMoment.diff(secondMoment, "days", true)));
}

/**
 * Return the difference between the two date in months no matter the years or hours.
 * @param firstDate
 * @param secondDate
 * @return number of months.
 */
function monthDiffBetweenDates(firstDate: Date, secondDate: Date): number {
  const firstMoment = dayjs(firstDate)
    .date(1)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);
  const secondMoment = dayjs(secondDate)
    .date(1)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);
  return Math.ceil(Math.abs(firstMoment.diff(secondMoment, "months", true)));
}

async function sleep(time: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, time));
}

/**
 * @param dateStr - Format must be actually "DD/MM/YYYY"
 * @return The JS Date computed from the string
 */
function getDateFromString(dateStr: string): Date | null {
  if (dateStr) {
    const birthday = dayjs.utc(dateStr, "DD/MM/YYYY", true);
    return birthday && birthday.isValid() ? birthday.toDate() : null;
  }
  return null;
}

function serializeToDateString(date: Date | null): string | null {
  if (!date) return null;

  return (
    date.getFullYear() +
    "-" +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + date.getDate()).slice(-2)
  );
}

function computeDateTime(date: Date, time: Date): Date {
  const dayjsDate = dayjs(date);
  const dayjsTime = dayjs(time);
  const combinedDateTime = dayjsDate
    .hour(dayjsTime.hour())
    .minute(dayjsTime.minute())
    .second(0)
    .millisecond(0);
  return combinedDateTime.toDate();
}

export {
  computeDateTime,
  dayDiffBetweenDates,
  durationBetweenDates,
  getDateFromString,
  hoursDiffBetweenDates,
  monthDiffBetweenDates,
  serializeToDateString,
  sleep
};
